import { useEffect, useMemo, useState, useCallback } from "react";
import { motion as Motion } from "framer-motion";
import { Link } from "react-router-dom";

import api from "@/utils/axios";
import i18n from "@/i18n";

import cls from "./MemberNotification.module.scss";

// Same placeholder hero images used in Wallet.jsx
import mrImg from "@/assets/UI/memberCreate/q21Mr.webp";
import mrMImg from "@/assets/UI/memberCreate/q21MrM.webp";
import mrsImg from "@/assets/UI/memberCreate/q21Mrs.webp";
import mrsMImg from "@/assets/UI/memberCreate/q21MrsM.webp";

const PAGE_SIZE = 10;

const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

const fadeIn = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

const listStagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05 } },
};

function formatRelativeTime(dateInput) {
  if (!dateInput) return "";
  const created =
    typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (Number.isNaN(created.getTime())) return "";

  const now = new Date();
  const diffMs = now - created;
  if (diffMs < 0) return "";

  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "just now";

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min${diffMin > 1 ? "s" : ""} ago`;

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? "s" : ""} ago`;

  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;

  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12)
    return `${diffMonth} month${diffMonth > 1 ? "s" : ""} ago`;

  const diffYear = Math.floor(diffMonth / 12);
  return `${diffYear} year${diffYear > 1 ? "s" : ""} ago`;
}

export default function MemberNotification() {
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [error, setError] = useState("");

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (key, fallback, opts) =>
    i18n.t(key, { defaultValue: fallback, ...(opts || {}) });

  const fetchPage = useCallback(async (pageToLoad, append = false) => {
    try {
      if (pageToLoad === 1) setLoading(true);
      else setLoadingMore(true);

      setError("");

      const res = await api.get("/v1/member/notifications", {
        params: { page: pageToLoad, limit: PAGE_SIZE },
      });

      const data = res.data || {};
      const newItems = data.notifications || [];

      setNotifications((prev) => (append ? [...prev, ...newItems] : newItems));
      setHasMore(!!data.hasMore);
      setUnreadCount(data.unreadCount ?? 0);
      setPage(pageToLoad);
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          t(
            "memberNotification.error.generic",
            "Something went wrong. Please try again.",
          ),
      );
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  const handleSeeMore = () => {
    if (!hasMore || loadingMore) return;
    fetchPage(page + 1, true);
  };

  const handleMarkRead = async (id) => {
    try {
      await api.patch(`/v1/member/notifications/${id}/read`);

      setNotifications((prev) =>
        prev.map((n) =>
          n._id === id || n.id === id
            ? {
                ...n,
                isRead: true,
                readAt: n.readAt || new Date().toISOString(),
              }
            : n,
        ),
      );

      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          t(
            "memberNotification.error.markRead",
            "Could not mark as read. Please try again.",
          ),
      );
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setMarkingAll(true);
      setError("");

      await api.patch("/v1/member/notifications/mark-all-read");

      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          isRead: true,
          readAt: n.readAt || new Date().toISOString(),
        })),
      );
      setUnreadCount(0);
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          t(
            "memberNotification.error.markAllRead",
            "Could not mark all as read. Please try again.",
          ),
      );
    } finally {
      setMarkingAll(false);
    }
  };

  const hasNotifications = notifications.length > 0;

  const renderCard = (notif) => {
    const id = notif._id || notif.id;
    const type = notif.type || "default";
    const isRead = !!notif.isRead;

    const createdAt = notif.createdAt ? new Date(notif.createdAt) : null;
    const relativeTime = createdAt ? formatRelativeTime(createdAt) : "";

    const isPaymentApproved = type === "payment_approved";
    const isPaymentDecline = type === "payment_decline";

    let toneClass = cls.toneDefault;
    if (isPaymentApproved) toneClass = cls.toneGood;
    else if (isPaymentDecline) toneClass = cls.toneBad;

    const baseTitle = notif.title || "";
    const baseDescription = notif.description || "";

    const displayTitle = isPaymentApproved
      ? t(
          "memberNotification.types.payment_approved.title",
          "Payment approved ✅",
        )
      : isPaymentDecline
        ? t(
            "memberNotification.types.payment_decline.title",
            "Payment declined ⚠️",
          )
        : baseTitle ||
          t("memberNotification.types.default.title", "Notification");

    const displayDescription = isPaymentApproved
      ? t(
          "memberNotification.types.payment_approved.description",
          "Your payment has been approved and your wallet is updated.",
        )
      : isPaymentDecline
        ? t(
            "memberNotification.types.payment_decline.description",
            "Your payment was declined. Please check details and submit again.",
          )
        : baseDescription;

    return (
      <Motion.article
        key={id}
        className={`${cls.notifCard} ${toneClass} ${
          isRead ? cls.cardRead : ""
        }`}
        variants={fadeIn}
      >
        <div className={cls.cardGlow} aria-hidden="true" />
        <div className={cls.cardInner}>
          <div className={cls.cardTop}>
            <div className={cls.cardTitleRow}>
              <h3 className={cls.cardTitle}>{displayTitle}</h3>
              <span
                className={`${cls.readPill} ${
                  isRead ? cls.readPillRead : cls.readPillUnread
                }`}
              >
                {isRead
                  ? t("memberNotification.status.read", "Read")
                  : t("memberNotification.status.unread", "Unread")}
              </span>
            </div>

            <div className={cls.cardMetaRow}>
              {relativeTime ? (
                <span className={cls.cardTime}>{relativeTime}</span>
              ) : null}
              {notif.category ? (
                <span className={cls.metaPill}>{notif.category}</span>
              ) : (
                <span className={cls.metaPill}>
                  {t("memberNotification.meta.system", "System")}
                </span>
              )}
            </div>
          </div>

          {displayDescription ? (
            <p className={cls.cardDesc}>{displayDescription}</p>
          ) : null}

          {notif.ctaLabel || notif.ctaPath || !isRead ? (
            <div className={cls.cardFooter}>
              <div className={cls.ctaLeft}>
                {notif.ctaPath ? (
                  <Link to={notif.ctaPath} className={cls.ctaLink}>
                    {notif.ctaLabel ||
                      t("memberNotification.actions.open", "Open")}
                  </Link>
                ) : notif.ctaLabel ? (
                  <span className={cls.ctaStatic}>{notif.ctaLabel}</span>
                ) : null}
              </div>

              {!isRead ? (
                <button
                  type="button"
                  className={cls.markReadBtn}
                  onClick={() => handleMarkRead(id)}
                >
                  {t("memberNotification.actions.markRead", "Mark read")}
                </button>
              ) : (
                <span className={cls.readHint}>
                  {t("memberNotification.actions.done", "Done")}
                </span>
              )}
            </div>
          ) : null}
        </div>
      </Motion.article>
    );
  };

  return (
    <main className={cls.page}>
      {/* Fixed background stage (same as Wallet) */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      <div className={cls.pageInner}>
        <div className={cls.contentShell}>
          <div className={cls.shellCard}>
            {/* Header (Wallet style) */}
            <header className={cls.cardHeader}>
              <div className={cls.headerText}>
                <p className={cls.kicker}>
                  {t("memberNotification.kicker", "Member Inbox")}
                </p>

                <h1 className={cls.title}>
                  {t("memberNotification.title", "Notifications")}
                </h1>

                <p className={cls.subtitle}>
                  {t(
                    "memberNotification.subtitle",
                    "Updates, approvals, reminders, and system messages in one place.",
                  )}
                </p>

                <div className={cls.headerMeta}>
                  {error ? <p className={cls.error}>{error}</p> : null}
                  {loading && !hasNotifications ? (
                    <p className={cls.loading}>
                      {t(
                        "memberNotification.loading",
                        "Loading notifications…",
                      )}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className={cls.headerActions}>
                <span className={cls.unreadPill}>
                  {t("memberNotification.unreadCount", "{{count}} unread", {
                    count: unreadCount,
                  })}
                </span>

                <button
                  type="button"
                  className={cls.primaryButton}
                  disabled={markingAll || unreadCount === 0}
                  onClick={handleMarkAllRead}
                >
                  {markingAll
                    ? t(
                        "memberNotification.actions.markAllReadWorking",
                        "Marking…",
                      )
                    : t(
                        "memberNotification.actions.markAllRead",
                        "Mark all read",
                      )}
                </button>
              </div>
            </header>

            {/* Content */}
            {!loading && !hasNotifications && !error ? (
              <section className={cls.emptyState}>
                <div className={cls.emptyGlow} aria-hidden="true" />
                <p className={cls.emptyTitle}>
                  {t("memberNotification.empty.title", "No notifications yet")}
                </p>
                <p className={cls.emptyBody}>
                  {t(
                    "memberNotification.empty.body",
                    "When something important happens, it will show up here.",
                  )}
                </p>
              </section>
            ) : null}

            {hasNotifications ? (
              <>
                <Motion.section
                  className={cls.list}
                  variants={listStagger}
                  initial="hidden"
                  animate="show"
                >
                  {notifications.map(renderCard)}
                </Motion.section>

                <div className={cls.footerRow}>
                  {hasMore ? (
                    <button
                      type="button"
                      className={cls.secondaryButton}
                      onClick={handleSeeMore}
                      disabled={loadingMore}
                    >
                      {loadingMore
                        ? t(
                            "memberNotification.actions.loadingMore",
                            "Loading…",
                          )
                        : t("memberNotification.actions.seeMore", "See more")}
                    </button>
                  ) : (
                    <span className={cls.noMoreText}>
                      {t("memberNotification.noMore", "You’re all caught up.")}
                    </span>
                  )}
                </div>
              </>
            ) : null}
          </div>

          <div className={cls.bottomSpace} />
        </div>
      </div>
    </main>
  );
}
